import React, {
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  Loader,
  Send,
  UploadCloud,
  Receipt,
  Eye,
  Download,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const formatDate = (dateString) => {
  const d = new Date(dateString);
  return [
    String(d.getDate()).padStart(2, "0"),
    String(d.getMonth() + 1).padStart(2, "0"),
    d.getFullYear(),
  ].join(".");
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

/* Shell commun (même structure que Facility VIP) */
const Shell = ({ title, onBack, right, children }) => (
  <div className="fv-modal">
    <header className="fv-header">
      <button
        type="button"
        className="fv-back"
        aria-label="Back"
        onClick={onBack}
      />
      <h3 className="fv-title">{title}</h3>
      {right ?? <span className="fv-header-spacer" />}
    </header>
    <div className="fv-body">{children}</div>
  </div>
);

const ClientInvoices = ({ onClose }) => {
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [rect, setRect] = useState(null);

  /* upload reçu */
  const [uploadInvoice, setUploadInvoice] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  /* ── Zone du dashboard pour limiter le flou ── */
  useLayoutEffect(() => {
    const measure = () => {
      const el = document.querySelector(".overlayClient");
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setRect(null);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  /* ── Auth ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Math.floor(Date.now() / 1000)) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
      setCurrentUserId(decoded.id);
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  /* ── Fetch invoices ── */
  const fetchInvoices = useCallback(async () => {
    if (!currentUserId) return;
    setIsLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/invoices/user/${currentUserId}`,
        { headers: authHeaders() },
      );
      setInvoices(
        (data.invoices || [])
          .filter((inv) => inv.status === "pending")
          .sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate)),
      );
      setError(null);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to fetch invoices.";
      setError(msg);
      toast.error(msg);
      setInvoices([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  /* ── View PDF ── */
  const handleView = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/invoices/view/${invoiceId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to open invoice.");
    }
  };

  /* ── Download PDF ── */
  const handleDownload = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/invoices/download/${invoiceId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to download invoice.");
    }
  };

  /* ── Upload reçu ── */
  const closeUpload = () => {
    setUploadInvoice(null);
    setReceiptFile(null);
  };

  const handleSubmitReceipt = async () => {
    if (!receiptFile || !uploadInvoice || isSubmitting) return;
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("status", "pending");
    formData.append("receipt", receiptFile);
    formData.append("invoiceDate", uploadInvoice.invoiceDate);
    try {
      await axios.put(`${API_URL}/invoices/${uploadInvoice._id}`, formData, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      closeUpload();
      fetchInvoices();
      toast.success("Receipt sent to administrator!", {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit receipt.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── Render ── */
  return createPortal(
    <>
      <div
        className="fv-backdrop"
        style={{ borderRadius: 0 }}
        onClick={onClose}
      />

      {/* ── UPLOAD REÇU ── */}
      {uploadInvoice ? (
        <Shell
          title={`Invoice — ${formatDate(uploadInvoice.invoiceDate)}`}
          onBack={closeUpload}
        >
          <label
            htmlFor="receipt-upload"
            className={`fv-dropzone fv-dropzone-lg ${receiptFile ? "has-file" : ""}`}
          >
            <UploadCloud size={22} />
            <span>{receiptFile ? receiptFile.name : "Upload receipt"}</span>
            <small>{receiptFile ? "Click to change" : "JPG, PNG or PDF"}</small>
          </label>
          <input
            id="receipt-upload"
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
            style={{ display: "none" }}
          />

          <div className="fv-actions fv-actions-even">
            <button
              type="button"
              className="fv-btn fv-btn-ghost"
              onClick={closeUpload}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="fv-btn fv-btn-primary"
              onClick={handleSubmitReceipt}
              disabled={!receiptFile || isSubmitting}
            >
              {isSubmitting ? (
                <Loader className="fv-spin" size={13} />
              ) : (
                <Send size={13} />
              )}{" "}
              Submit
            </button>
          </div>
        </Shell>
      ) : (
        /* ── LISTE DES FACTURES ── */
        <Shell
          title="My Invoices"
          onBack={onClose}
          right={<span className="fv-count">{invoices.length}</span>}
        >
          {error && <div className="fv-error">{error}</div>}

          {!error && isLoading ? (
            <div className="fv-state">
              <Loader className="fv-spin" size={16} /> Loading…
            </div>
          ) : !error && invoices.length > 0 ? (
            <div className="fv-reports">
              {invoices.map((inv, i) => (
                <div key={inv._id || i} className="fv-report fv-invoice">
                  <span className="fv-report-icon">
                    <Receipt size={15} />
                  </span>
                  <span className="fv-report-text">
                    <strong>{formatDate(inv.invoiceDate)}</strong>
                    <small className="fv-pending">Pending</small>
                  </span>

                  <button
                    type="button"
                    className="fv-icon-btn fv-icon-btn-upload"
                    title="Upload payment receipt"
                    onClick={() => {
                      setUploadInvoice(inv);
                      setReceiptFile(null);
                    }}
                  >
                    <UploadCloud size={14} />
                  </button>
                  <button
                    type="button"
                    className="fv-icon-btn"
                    title="View"
                    onClick={() => handleView(inv._id)}
                  >
                    <Eye size={14} />
                  </button>
                  <button
                    type="button"
                    className="fv-icon-btn"
                    title="Download"
                    onClick={() => handleDownload(inv._id)}
                  >
                    <Download size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            !error && <div className="fv-state">No pending invoices found.</div>
          )}
        </Shell>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        style={{ zIndex: 10050 }}
      />
    </>,
    document.body,
  );
};

export default ClientInvoices;
